import React, { useState, useMemo } from 'react';
import { Undo2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';

const fmtRs = (n) => `Rs. ${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function RefundModal({ sale, items = [], refunds = [], onClose, onRefund }) {
  const [quantities, setQuantities] = useState({});
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const refundedQtyMap = useMemo(() => {
    const map = {};
    refunds.forEach((r) => {
      (r.items || []).forEach((it) => {
        map[it.itemId] = (map[it.itemId] || 0) + it.quantityRefunded;
      });
    });
    return map;
  }, [refunds]);

  const maxQtyMap = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      map[it.itemId] = Math.max(0, it.quantity - (refundedQtyMap[it.itemId] || 0));
    });
    return map;
  }, [items, refundedQtyMap]);

  const setQty = (itemId, val) => {
    const max = maxQtyMap[itemId] || 0;
    const num = Math.max(0, Math.min(max, Number(val) || 0));
    setQuantities((p) => ({ ...p, [itemId]: num }));
  };

  const refundTotal = useMemo(
    () =>
      items.reduce((sum, it) => {
        const q = quantities[it.itemId] || 0;
        return sum + q * (it.unitPrice || 0);
      }, 0),
    [items, quantities]
  );

  const handleSubmit = async () => {
    const selected = items.filter((it) => (quantities[it.itemId] || 0) > 0).map((it) => ({
      itemId: it.itemId,
      productId: it.productId,
      quantity: quantities[it.itemId],
    }));
    if (selected.length === 0) {
      toast.error('Select at least one item to refund');
      return;
    }
    setLoading(true);
    try {
      await onRefund({ items: selected, reason: reason.trim() || undefined });
      toast.success('Refund recorded successfully');
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Refund failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Refund Items" description={`Order ${sale?.transactionId || ''}`} size="md">
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700/40 text-xs text-gray-500 dark:text-gray-400 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Product</th>
              <th className="px-3 py-2 font-medium text-center">Sold</th>
              <th className="px-3 py-2 font-medium text-center">Refunded</th>
              <th className="px-3 py-2 font-medium text-center">Return</th>
              <th className="px-3 py-2 font-medium text-right">Refund</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const max = maxQtyMap[it.itemId] || 0;
              const qty = quantities[it.itemId] || 0;
              return (
                <tr key={it.itemId} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-3 py-2">
                    <p className="font-medium text-gray-900 dark:text-white">{it.productName}</p>
                    <p className="text-xs text-gray-400">{fmtRs(it.unitPrice)} each</p>
                  </td>
                  <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-300">{it.quantity}</td>
                  <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-300">{refundedQtyMap[it.itemId] || 0}</td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="number"
                      min={0}
                      max={max}
                      value={qty || ''}
                      disabled={max <= 0}
                      onChange={(e) => setQty(it.itemId, e.target.value)}
                      placeholder={max <= 0 ? '—' : '0'}
                      className="w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-white">
                    {qty > 0 ? fmtRs(qty * (it.unitPrice || 0)) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {items.some((it) => (maxQtyMap[it.itemId] || 0) <= 0 && (refundedQtyMap[it.itemId] || 0) > 0) && (
        <p className="flex items-start gap-1.5 text-xs text-gray-400 dark:text-gray-500 mt-3">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
          Some items are already fully refunded.
        </p>
      )}

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional)"
        rows={2}
        className="mt-3 w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
      />

      <div className="flex items-center justify-between mt-4">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          Refund total: {fmtRs(refundTotal)}
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="danger" icon={Undo2} loading={loading} onClick={handleSubmit}>
            Confirm Refund
          </Button>
        </div>
      </div>
    </Modal>
  );
}